import React, { useState, useRef } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { Button } from "@/components/ui/Button";
import { MealOptionType } from "@/types/db/shifts";
import useContextMenu from "@/hooks/useContextMenu";

export interface MealOptionEditorProps {
  items: MealOptionType[];
  editing?: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, patch: Partial<MealOptionType>) => void;
}

interface DropdownFieldProps {
  label: string;
  value: string;
  options: { value: string | number; label: string }[];
  onSelect: (value: any) => void;
  editing: boolean;
}

function DropdownField({ label, value, options, onSelect, editing }: DropdownFieldProps) {
  const { colors } = useTheme();
  const contextMenu = useContextMenu();
  const buttonRef = useRef<View>(null);

  const menuContent = (
    <View style={[styles.enumMenu, { backgroundColor: colors.background, borderColor: colors.border }]}>
      {options.map((option) => (
        <Button
          key={option.value}
          text={option.label}
          style={styles.enumMenuItem}
          onPress={() => {
            onSelect(option.value);
            contextMenu.setVisible(false);
          }}
        />
      ))}
    </View>
  );

  if (!editing) {
    return (
      <View style={styles.fieldGroup}>
        <ThemedText style={styles.label}>{label}</ThemedText>
        <ThemedText style={styles.value}>{value}</ThemedText>
      </View>
    );
  }

  return (
    <View style={styles.fieldGroup}>
      <ThemedText style={styles.label}>{label}</ThemedText>
      <Button
        ref={buttonRef}
        text={value}
        icon="chevron-down"
        style={{ gap: 4, backgroundColor: "transparent", paddingHorizontal: 6, height: 28 }}
        textStyle={{ fontSize: 13 }}
        iconPosition="right"
        onPress={() => {
          contextMenu.setIgnoreRef(buttonRef);
          buttonRef.current?.measureInWindow((pageX, pageY) =>
            contextMenu.setLocation({ x: pageX - 20, y: pageY + 30 }),
          );
          contextMenu.setMenuContent(menuContent);
          contextMenu.setVisible(true);
          contextMenu.setStyle({ backgroundColor: colors.background, overflow: "hidden" });
        }}
      />
    </View>
  );
}

export function MealOptionEditor({ items, editing = false, onAdd, onRemove, onUpdate }: MealOptionEditorProps) {
  const { colors } = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const typeOptions = [
    { value: "UNPAID", label: "Unpaid" },
    { value: "PAID", label: "Paid" },
  ];

  const durationOptions = [
    { value: 30, label: "30 min" },
    { value: 60, label: "60 min" },
    { value: 120, label: "2 h" },
  ];

  const afterOptions = [
    { value: 0, label: "Start" },
    { value: 4, label: "4h" },
    { value: 5, label: "5h" },
    { value: 6, label: "6h" },
  ];

  const paidByOptions = [
    { value: "CLIENT", label: "Client" },
    { value: "TENANT", label: "Tenant" },
  ];

  const formatType = (type: string) => (type === "PAID" ? "Paid" : "Unpaid");
  const formatDuration = (duration: number) => (duration > 60 ? `${duration / 60} h` : `${duration} min`);
  const formatAfter = (after: number) => (after === 0 ? "Start" : `${after}h`);
  const formatPaidBy = (paidBy?: string) => (paidBy === "CLIENT" ? "Client" : "Tenant");

  if (!editing && (!items || items.length === 0)) {
    return <ThemedText style={{ opacity: 0.5 }}>No meal breaks</ThemedText>;
  }

  return (
    <View style={styles.container}>
      {items.map((item, index) => (
        <View
          key={index}
          onPointerEnter={() => setHoveredIndex(index)}
          onPointerLeave={() => setHoveredIndex(null)}
          style={[
            styles.item,
            {
              backgroundColor: colors.card,
              borderColor: colors.border,
            },
          ]}
        >
          <View style={styles.itemContent}>
            <DropdownField
              label="Type:"
              value={formatType(item.type)}
              options={typeOptions}
              onSelect={(value) => onUpdate(index, { type: value })}
              editing={editing}
            />

            <DropdownField
              label="Duration:"
              value={formatDuration(item.duration)}
              options={durationOptions}
              onSelect={(value) => onUpdate(index, { duration: value })}
              editing={editing}
            />

            <DropdownField
              label="After:"
              value={formatAfter(item.after)}
              options={afterOptions}
              onSelect={(value) => onUpdate(index, { after: value })}
              editing={editing}
            />

            {item.type === "PAID" && (
              <DropdownField
                label="Paid By:"
                value={formatPaidBy(item.paid_by)}
                options={paidByOptions}
                onSelect={(value) => onUpdate(index, { paid_by: value })}
                editing={editing}
              />
            )}
          </View>

          {/* Remove button */}
          {editing && (
            <TouchableOpacity onPress={() => onRemove(index)} style={styles.removeButton}>
              {hoveredIndex === index && (
                <MaterialCommunityIcons name="close-circle" size={20} color={colors.textSecondary} />
              )}
            </TouchableOpacity>
          )}
        </View>
      ))}

      {/* Add button */}
      {editing && (
        <Button
          text="Add Meal Break"
          icon="plus"
          iconPosition="right"
          style={[
            styles.addButton,
            {
              borderColor: colors.border,
              backgroundColor: "transparent",
            },
          ]}
          textStyle={{ color: colors.text, fontSize: 13 }}
          iconColor={colors.text}
          onPress={onAdd}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  item: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  itemContent: {
    flex: 1,
    flexDirection: "row",
    gap: 8,
  },
  fieldGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    opacity: 0.7,
  },
  value: {
    fontSize: 12,
  },
  removeButton: {
    padding: 4,
    marginLeft: 8,
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
  },
  addButton: {
    height: 36,
    borderWidth: 1,
    borderStyle: "dashed",
  },
  enumMenu: {
    borderWidth: 0,
  },
  enumMenuItem: {
    backgroundColor: "transparent",
    borderWidth: 0,
    justifyContent: "flex-start",
    gap: 8,
  },
});
